
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToast } from '@/hooks/use-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/integrations/supabase/client';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

const formSchema = z.object({
  fullName: z.string().min(2, { message: 'Name muss mindestens 2 Zeichen lang sein' }),
  email: z.string().email({ message: 'Bitte gib eine gültige E-Mail-Adresse ein' }),
  website: z.string().optional(),
  targetAudience: z.string().min(5, { message: 'Bitte beschreibe deine Zielgruppe (min. 5 Zeichen)' }),
  motivation: z.string().min(10, { message: 'Bitte beschreibe deine Motivation (min. 10 Zeichen)' }),
  termsAccepted: z.boolean().refine(val => val === true, { 
    message: 'Du musst den Bedingungen zustimmen, um fortzufahren' 
  }),
});

type FormData = z.infer<typeof formSchema>;

interface PartnerApplicationFormProps {
  onSuccess?: () => void;
}

const PartnerApplicationForm: React.FC<PartnerApplicationFormProps> = ({ onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: '',
      email: '',
      website: '',
      targetAudience: '',
      motivation: '',
      termsAccepted: false,
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    try {
      const { data: application, error } = await supabase
        .from('partner_applications')
        .insert({
          full_name: data.fullName,
          email: data.email,
          website: data.website || null,
          target_audience: data.targetAudience,
          motivation: data.motivation,
        })
        .select();
      
      if (error) throw error;
      
      toast({
        title: "Bewerbung eingereicht",
        description: "Vielen Dank für deine Bewerbung. Wir prüfen deinen Antrag manuell und melden uns zeitnah per E-Mail.",
      });
      
      form.reset();
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: "Fehler",
        description: "Die Bewerbung konnte leider nicht gesendet werden. Bitte versuche es später erneut.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Dein vollständiger Name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>E-Mail</FormLabel>
              <FormControl>
                <Input type="email" placeholder="deine@email.de" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="website"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Website oder Social Media (optional)</FormLabel>
              <FormControl>
                <Input placeholder="https://deine-website.de oder @dein_handle" {...field} />
              </FormControl>
              <FormDescription>
                Deine Website oder Social-Media-Profile
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="targetAudience"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Zielgruppe</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Beschreibe deine Zielgruppe" 
                  {...field} 
                />
              </FormControl>
              <FormDescription>
                Mit welcher Zielgruppe arbeitest du oder wen kannst du erreichen?
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="motivation"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Motivation</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Warum möchtest du CHRONA-Partner werden?" 
                  className="min-h-[100px]"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="termsAccepted"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>
                  Ich stimme den Bedingungen für Partnerschaften zu
                </FormLabel>
                <FormDescription>
                  Du stimmst den Bedingungen für Partnerschaften und der Provisionsregelung zu.
                </FormDescription>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <Button 
          type="submit" 
          className="w-full" 
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Wird gesendet...
            </>
          ) : (
            'Bewerbung abschicken'
          )}
        </Button>
      </form>
    </Form>
  );
};

export default PartnerApplicationForm;
